/**
 * VerificationFindingsCard — at-a-glance Verification Findings panel.
 *
 * Bucketizes the requested NTN reports into four verification themes:
 *   • Employment Verified         (employment-verification reports)
 *   • Landlord Verified           (rental-history reports)
 *   • Income Reviewed             (credit / income reports)
 *   • Documents Reviewed          (eviction / criminal / sex offender / docs)
 *
 * Each tile shows a status indicator + concise summary. Mounted on Applicant
 * and Household profiles directly under the Executive Summary stripe.
 */
import { CheckCircle2, Clock, AlertTriangle, Circle, Briefcase, Home, BadgeDollarSign, FileCheck2 } from "lucide-react";

// Map report types to verification themes.
// Only themes whose report types are ordered on the screening render — the
// display is order-driven so PMs never see phantom "pending" work for
// services they did not request.
const THEME = {
  employment: {
    label: "Employment Verification",
    icon: Briefcase,
    types: ["employment_verification"],
    eyebrow: "Income Source",
  },
  landlord: {
    label: "Landlord Verification",
    icon: Home,
    // NEW `landlord_verification` (Feb 2026) + legacy `eviction_search` for
    // pre-refactor screenings that ordered the old bundle.
    types: ["landlord_verification", "eviction_search", "rental_history", "eviction"],
    eyebrow: "Tenancy History",
  },
  income: {
    label: "DecisionPoint Report",
    icon: BadgeDollarSign,
    types: ["credit_report", "credit"],
    eyebrow: "Financial Intelligence",
  },
  documents: {
    label: "Document Validation",
    icon: FileCheck2,
    types: ["document_validation", "criminal_report", "ssn_verification",
            "criminal", "sex_offender", "id_verification"],
    eyebrow: "Identity & Background",
  },
};

function summarize(reports) {
  // Returns the aggregate state of the bucket: complete | in_progress | flagged | pending | not_requested.
  const requested = reports.filter((r) => r.requested);
  if (requested.length === 0) return { state: "not_requested", n: 0, total: 0 };
  const flagged = requested.filter((r) => r.status === "flagged").length;
  const complete = requested.filter((r) => r.status === "complete").length;
  const inprog = requested.filter((r) => r.status === "in_progress").length;
  if (flagged > 0) return { state: "flagged", n: flagged, total: requested.length };
  if (complete === requested.length) return { state: "complete", n: complete, total: requested.length };
  if (inprog > 0 || complete > 0) return { state: "in_progress", n: complete, total: requested.length };
  return { state: "pending", n: 0, total: requested.length };
}

const STATE_VISUAL = {
  complete:      { icon: CheckCircle2, ring: "ring-emerald-200", bg: "bg-emerald-50",  text: "text-emerald-700",  dot: "bg-emerald-500",  badge: "Verified",      desc: "Confirmed by NTN" },
  in_progress:   { icon: Clock,        ring: "ring-blue-200",    bg: "bg-blue-50",     text: "text-blue-700",     dot: "bg-blue-500",     badge: "In Progress",   desc: "Verification underway" },
  flagged:       { icon: AlertTriangle,ring: "ring-rose-200",    bg: "bg-rose-50",     text: "text-rose-700",     dot: "bg-rose-500",     badge: "Needs Review",  desc: "Anomaly observed" },
  pending:       { icon: Circle,       ring: "ring-slate-200",   bg: "bg-slate-50",    text: "text-slate-600",    dot: "bg-slate-400",    badge: "Pending",       desc: "Awaiting start" },
  not_requested: { icon: Circle,       ring: "ring-slate-100",   bg: "bg-white",       text: "text-slate-400",    dot: "bg-slate-200",    badge: "Not Requested", desc: "Out of scope" },
};

export default function VerificationFindingsCard({ screening, testid = "verification-findings" }) {
  const reports = screening.reports || [];

  // Order-driven: build a theme tile only when the client actually ordered
  // at least one report in that theme. Un-ordered themes are omitted
  // entirely so PMs never see phantom "Out of Scope" cards for work they
  // did not request.
  const themes = Object.entries(THEME)
    .map(([key, def]) => {
      const subset = reports.filter((r) => def.types.includes(r.type) && r.requested);
      const summary = summarize(subset);
      return { key, def, summary, subset };
    })
    .filter((t) => t.subset.length > 0);

  // Aggregate counts shown in header.
  const verifiedCount = themes.filter((t) => t.summary.state === "complete").length;
  const flaggedCount = themes.filter((t) => t.summary.state === "flagged").length;

  // Empty state — no ordered themes at all. Rare but possible for a screening
  // that has been created but not yet ordered.
  if (themes.length === 0) {
    return (
      <section className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm p-6 text-center text-sm text-slate-500" data-testid={`${testid}-empty`}>
        No verification services ordered on this screening.
      </section>
    );
  }

  return (
    <section className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm overflow-hidden" data-testid={testid}>
      <header className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-full bg-emerald-50 ring-1 ring-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="h-4 w-4 text-emerald-700" />
          </div>
          <div>
            <div className="ntn-eyebrow">
              Verification Findings
            </div>
            <div className="ntn-h3 mt-0.5">What NTN Confirmed</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {flaggedCount > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-rose-50 text-rose-700 ring-1 ring-rose-200 rounded-full ntn-eyebrow" data-testid="verification-flagged-count">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> {flaggedCount} Need{flaggedCount === 1 ? "s" : ""} Review
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 rounded-full ntn-eyebrow" data-testid="verification-verified-count">
            <CheckCircle2 className="h-3 w-3" /> {verifiedCount} Verified
          </span>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-100">
        {themes.map(({ key, def, summary }) => {
          const v = STATE_VISUAL[summary.state];
          const ThemeIcon = def.icon;
          const StateIcon = v.icon;
          return (
            <div key={key} className="bg-white p-5" data-testid={`verification-theme-${key}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className={`h-9 w-9 rounded-md ${v.bg} ring-1 ${v.ring} flex items-center justify-center`}>
                  <ThemeIcon className={`h-4 w-4 ${v.text}`} />
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ring-1 ${v.ring} ${v.bg} ${v.text} ntn-eyebrow`}>
                  <StateIcon className="h-2.5 w-2.5" /> {v.badge}
                </span>
              </div>
              <div className="ntn-eyebrow">{def.eyebrow}</div>
              <div className="ntn-h3 mt-1">{def.label}</div>
              <div className="ntn-caption mt-1.5">{v.desc}</div>
              {summary.total > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 ntn-mono">
                  <span className={`h-1.5 w-1.5 rounded-full ${v.dot}`} />
                  {`${summary.n} of ${summary.total} reports`}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
